/**
 * DueDateEditor — inline Date-Picker für projects.due_date (tool-intern, manuell).
 * Quelle: Plan 2026-05-16 — User wollte Fälligkeit setzen können in Project-Detail.
 * Pattern 1:1 wie ResponsibilityPicker (5 invalidate-Keys, Toast, Reader-Disable).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';
import { CommittedDateInput } from './committed-date-input.js';

export function DueDateEditor({
  projectId,
  value,
  canEdit,
  onSaved,
}: {
  projectId: string;
  value: string | null | undefined;
  canEdit: boolean;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mut = useMutation({
    mutationFn: (next: string | null) => api.patchProject(projectId, { due_date: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['v2-project', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['v2-mc-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-board-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-atlas-projects', 'all'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-handover-projects'] });
      toast.success(t('v2.duedate.saved', 'Fälligkeit gespeichert'));
      onSaved?.();
    },
    onError: (err) =>
      toast.error(
        t('v2.duedate.failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <div className="flex items-center gap-2">
      <CommittedDateInput
        value={value}
        onCommit={(next) => mut.mutate(next)}
        canEdit={canEdit}
        busy={mut.isPending}
      />
      {value && canEdit && (
        <button
          type="button"
          onClick={() => mut.mutate(null)}
          disabled={mut.isPending}
          title={t('v2.duedate.clear', 'Datum löschen')}
          className="text-[11px] px-1.5 py-0.5 rounded"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-3)',
            border: '1px solid var(--line-soft)',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}
